import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type Theme = 'light' | 'dark';

const THEME_STORAGE_KEY = 'app-theme';
const DEFAULT_THEME: Theme = 'light';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private currentTheme$ = new BehaviorSubject<Theme>(DEFAULT_THEME);
  private document: Document;
  private onThemeChangeCallback?: (theme: Theme) => void;

  constructor(@Inject(DOCUMENT) private doc: Document) {
    this.document = doc;
  }

  /**
   * Inicializa o tema, carregando do localStorage ou usando o padrão
   */
  public initTheme(): void {
    const savedTheme = this.getStoredTheme();
    const theme = savedTheme || DEFAULT_THEME;
    this.setTheme(theme, false);
  }

  /**
   * Inicializa o tema com base nas preferências do usuário
   * @param userThemeMode - Modo de tema do backend ('LIGHT' ou 'DARK')
   */
  public initThemeFromUser(userThemeMode?: string): void {
    if (userThemeMode) {
      const theme = this.backendToFrontendTheme(userThemeMode);
      this.setTheme(theme, true);
    } else {
      // Fallback para localStorage ou tema padrão
      this.initTheme();
    }
  }

  /**
   * Define o tema atual
   * @param theme - Nome do tema ('light' ou 'dark')
   * @param saveToStorage - Se deve salvar no localStorage (padrão: true)
   */
  public setTheme(theme: Theme, saveToStorage: boolean = true): void {
    if (saveToStorage) {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    }

    // Remove classes de tema anteriores
    this.document.body.classList.remove('theme-light', 'theme-dark', 'theme-evollo', 'theme-kainos');

    // Adiciona a nova classe de tema
    this.document.body.classList.add(`theme-${theme}`);

    // Atualiza o BehaviorSubject
    this.currentTheme$.next(theme);
  }

  /**
   * Retorna o tema atual
   */
  public getCurrentTheme(): Theme {
    return this.currentTheme$.value;
  }

  /**
   * Observable do tema atual
   */
  public getTheme$(): Observable<Theme> {
    return this.currentTheme$.asObservable();
  }

  /**
   * Alterna entre os temas disponíveis
   */
  public toggleTheme(): void {
    const currentTheme = this.getCurrentTheme();
    const newTheme: Theme = currentTheme === 'light' ? 'dark' : 'light';
    this.setTheme(newTheme);
    
    // Notifica callback se existir (UserService)
    if (this.onThemeChangeCallback) {
      this.onThemeChangeCallback(newTheme);
    }
  }

  /**
   * Registra callback para ser notificado quando o tema mudar
   * @param callback - Função a ser chamada quando o tema mudar
   */
  public setOnThemeChangeCallback(callback: (theme: Theme) => void): void {
    this.onThemeChangeCallback = callback;
  }

  /**
   * Retorna o tema armazenado no localStorage
   * Suporta migração de temas antigos (evollo/kainos) para novos (light/dark)
   */
  private getStoredTheme(): Theme | null {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    
    // Migração de temas antigos
    if (stored === 'evollo') {
      localStorage.setItem(THEME_STORAGE_KEY, 'light');
      return 'light';
    }
    if (stored === 'kainos') {
      localStorage.setItem(THEME_STORAGE_KEY, 'dark');
      return 'dark';
    }
    
    if (stored === 'light' || stored === 'dark') {
      return stored as Theme;
    }
    return null;
  }

  /**
   * Converte formato do backend (LIGHT/DARK) para formato do frontend (light/dark)
   * @param backendTheme - Tema no formato do backend
   * @returns Tema no formato do frontend
   */
  public backendToFrontendTheme(backendTheme: string): Theme {
    const normalized = backendTheme?.toUpperCase();
    if (normalized === 'DARK') {
      return 'dark';
    }
    return 'light'; // default para LIGHT ou valores inválidos
  }

  /**
   * Converte formato do frontend (light/dark) para formato do backend (LIGHT/DARK)
   * @param theme - Tema no formato do frontend
   * @returns Tema no formato do backend
   */
  public frontendToBackendTheme(theme: Theme): 'LIGHT' | 'DARK' {
    return theme === 'dark' ? 'DARK' : 'LIGHT';
  }
}

